import React, { useEffect, useRef, useMemo } from 'react';
import gsap from 'gsap';

/* ───────────────────────────────────────────────
   ANIMATED WATER / WAVE BACKGROUND
   ─────────────────────────────────────────────── */
export function WaterBackground({ color1 = '#1a8fd1', color2 = '#0e5f8a', waveCount = 3, children, style }) {
  const id = useMemo(() => `water-${Math.random().toString(36).slice(2, 8)}`, []);
  return (
    <div style={{ position: 'relative', overflow: 'hidden', ...style }}>
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 1200 400" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`${id}-grad`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color1} />
            <stop offset="100%" stopColor={color2} />
          </linearGradient>
        </defs>
        <rect width="1200" height="400" fill={`url(#${id}-grad)`} />
        {Array.from({ length: waveCount }).map((_, i) => {
          const y = 80 + i * 90;
          const opacity = 0.08 + i * 0.04;
          return (
            <path key={i}
              d={`M0,${y} C200,${y - 20} 400,${y + 20} 600,${y} C800,${y - 20} 1000,${y + 20} 1200,${y} V400 H0 Z`}
              fill="white" opacity={opacity}>
              <animate attributeName="d"
                values={`M0,${y} C200,${y - 20} 400,${y + 20} 600,${y} C800,${y - 20} 1000,${y + 20} 1200,${y} V400 H0 Z;M0,${y + 10} C200,${y + 10} 400,${y - 10} 600,${y + 10} C800,${y + 10} 1000,${y - 10} 1200,${y + 10} V400 H0 Z;M0,${y} C200,${y - 20} 400,${y + 20} 600,${y} C800,${y - 20} 1000,${y + 20} 1200,${y} V400 H0 Z`}
                dur={`${4 + i * 1.5}s`} repeatCount="indefinite" />
            </path>
          );
        })}
      </svg>
      {children}
    </div>
  );
}

/* ───────────────────────────────────────────────
   DETAILED CANOE WITH VOYAGEUR
   ─────────────────────────────────────────────── */
export function VoyageurCanoe({ color = '#E03131', size = 120, direction = 1, animate = true }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!animate || !ref.current) return;
    gsap.to(ref.current, { y: -3, duration: 0.8, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    const paddle = ref.current.querySelector('.paddle');
    if (paddle) gsap.to(paddle, { rotation: -15, transformOrigin: '50% 0%', duration: 0.5, yoyo: true, repeat: -1, ease: 'sine.inOut' });
  }, [animate]);
  return (
    <div ref={ref} style={{ transform: direction < 0 ? 'scaleX(-1)' : undefined }}>
      <svg width={size} height={size * 0.7} viewBox="0 0 200 140" fill="none">
        {/* Water splash behind canoe */}
        <ellipse cx="20" cy="120" rx="12" ry="4" fill="white" opacity="0.5">
          <animate attributeName="rx" values="12;16;12" dur="0.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0.2;0.5" dur="0.8s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="180" cy="118" rx="10" ry="3" fill="white" opacity="0.4">
          <animate attributeName="rx" values="10;14;10" dur="1s" repeatCount="indefinite" />
        </ellipse>
        {/* Canoe hull - detailed birch bark style */}
        <path d="M15,110 Q20,130 100,130 Q180,130 185,110 Q180,95 100,95 Q20,95 15,110 Z" fill={color} />
        <path d="M15,110 Q20,130 100,130 Q180,130 185,110" stroke="rgba(0,0,0,0.2)" strokeWidth="2" fill="none" />
        <path d="M25,108 Q100,100 175,108" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" fill="none" />
        <path d="M30,118 Q100,124 170,118" stroke="rgba(255,255,255,0.2)" strokeWidth="1" fill="none" />
        {/* Pointed bow & stern */}
        <path d="M10,108 L15,110 L10,112 Z" fill={color} />
        <path d="M190,108 L185,110 L190,112 Z" fill={color} />
        {/* Voyageur body */}
        <rect x="88" y="60" width="24" height="35" rx="6" fill={color} />
        <rect x="85" y="62" width="30" height="8" rx="3" fill="rgba(255,255,255,0.3)" />
        {/* Voyageur head */}
        <circle cx="100" cy="48" r="14" fill="#FFDAB9" />
        <ellipse cx="100" cy="44" rx="8" ry="5" fill="#8B4513" />
        {/* Toque / red hat */}
        <path d="M88,44 Q92,30 100,28 Q108,30 112,44" fill={color} />
        <circle cx="100" cy="27" r="4" fill="white" />
        {/* Eyes and smile */}
        <circle cx="95" cy="48" r="2" fill="#333" />
        <circle cx="105" cy="48" r="2" fill="#333" />
        <path d="M95,54 Q100,58 105,54" stroke="#333" strokeWidth="1.5" fill="none" />
        {/* Paddle */}
        <g className="paddle">
          <line x1="130" y1="45" x2="160" y2="115" stroke="#8B5A2B" strokeWidth="4" strokeLinecap="round" />
          <path d="M155,105 L165,125 Q160,130 155,125 L150,110 Z" fill="#8B5A2B" />
        </g>
        {/* Arms */}
        <line x1="110" y1="70" x2="132" y2="52" stroke="#FFDAB9" strokeWidth="5" strokeLinecap="round" />
        <line x1="90" y1="75" x2="135" y2="70" stroke="#FFDAB9" strokeWidth="5" strokeLinecap="round" />
      </svg>
    </div>
  );
}

/* ───────────────────────────────────────────────
   PINE TREE (detailed, layered)
   ─────────────────────────────────────────────── */
export function PineTree({ height = 80, x = 0, variant = 0 }) {
  const greens = ['#1B5E20', '#2E7D32', '#388E3C', '#1B4332', '#2D6A4F'];
  const trunkH = height * 0.2;
  const color = greens[variant % greens.length];
  const w = height * 0.45;
  return (
    <g transform={`translate(${x}, 0)`}>
      <rect x={-3} y={-trunkH} width={6} height={trunkH} fill="#5D4037" rx="2" />
      <polygon points={`0,${-height} ${-w * 0.4},${-height * 0.55} ${w * 0.4},${-height * 0.55}`} fill={color} />
      <polygon points={`0,${-height * 0.75} ${-w * 0.55},${-height * 0.35} ${w * 0.55},${-height * 0.35}`} fill={color} opacity="0.9" />
      <polygon points={`0,${-height * 0.55} ${-w * 0.7},${-height * 0.1} ${w * 0.7},${-height * 0.1}`} fill={color} opacity="0.85" />
      {/* Snow caps */}
      <polygon points={`0,${-height} ${-w * 0.15},${-height * 0.7} ${w * 0.15},${-height * 0.7}`} fill="white" opacity="0.35" />
    </g>
  );
}

/* ───────────────────────────────────────────────
   FOREST LINE (row of trees)
   ─────────────────────────────────────────────── */
export function ForestLine({ width = 1200, treeCount = 12, baseY = 0, minH = 40, maxH = 80, opacity = 1 }) {
  const trees = useMemo(() =>
    Array.from({ length: treeCount }).map((_, i) => ({
      x: (width / treeCount) * i + Math.random() * 30,
      h: minH + Math.random() * (maxH - minH),
      v: Math.floor(Math.random() * 5),
    })), [width, treeCount, minH, maxH]);
  return (
    <g opacity={opacity}>
      {trees.map((t, i) => <PineTree key={i} x={t.x} height={t.h} variant={t.v} />)}
    </g>
  );
}

/* ───────────────────────────────────────────────
   MOUNTAIN RANGE
   ─────────────────────────────────────────────── */
export function MountainRange({ width = 1200, baseY = 300, peaks = 5, maxHeight = 180, color = '#4A6741', snowLine = 0.3 }) {
  const id = useMemo(() => `mtn-${Math.random().toString(36).slice(2, 8)}`, []);
  const points = useMemo(() => {
    const pts = [{ x: 0, y: baseY }];
    const segW = width / peaks;
    for (let i = 0; i < peaks; i++) {
      const peakX = segW * i + segW * (0.3 + Math.random() * 0.4);
      const peakY = baseY - maxHeight * (0.5 + Math.random() * 0.5);
      const midX = segW * (i + 1);
      pts.push({ x: peakX - 20, y: baseY - maxHeight * 0.2 });
      pts.push({ x: peakX, y: peakY });
      pts.push({ x: midX, y: baseY - maxHeight * 0.15 + Math.random() * 20 });
    }
    pts.push({ x: width, y: baseY });
    return pts;
  }, [width, baseY, peaks, maxHeight]);
  const pathD = `M${points.map(p => `${p.x},${p.y}`).join(' L')} L${width},${baseY + 50} L0,${baseY + 50} Z`;
  return (
    <g>
      <defs>
        <linearGradient id={`${id}-g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={color} stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <path d={pathD} fill={`url(#${id}-g)`} />
      {/* Snow caps on top portions */}
      {points.filter((_, i) => i % 3 === 2).map((p, i) => (
        <ellipse key={i} cx={p.x} cy={p.y + 8} rx={18 + Math.random() * 10} ry={8} fill="white" opacity="0.6" />
      ))}
    </g>
  );
}

/* ───────────────────────────────────────────────
   ANIMATED CLOUDS
   ─────────────────────────────────────────────── */
export function AnimatedCloud({ x, y, scale = 1, speed = 30, width = 1200 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    gsap.fromTo(ref.current, { x: x }, { x: x + width + 100, duration: speed, repeat: -1, ease: 'none' });
  }, [x, speed, width]);
  return (
    <g ref={ref} transform={`translate(${x}, ${y}) scale(${scale})`} opacity="0.8">
      <ellipse cx="0" cy="0" rx="40" ry="15" fill="white" />
      <ellipse cx="-20" cy="-5" rx="25" ry="12" fill="white" />
      <ellipse cx="15" cy="-8" rx="30" ry="14" fill="white" />
      <ellipse cx="30" cy="2" rx="20" ry="10" fill="white" />
    </g>
  );
}

/* ───────────────────────────────────────────────
   SNOW PARTICLES (CSS-based for performance)
   ─────────────────────────────────────────────── */
export function SnowParticles({ count = 30 }) {
  const flakes = useMemo(() =>
    Array.from({ length: count }).map((_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 5 + Math.random() * 8,
      size: 2 + Math.random() * 4,
      opacity: 0.3 + Math.random() * 0.5,
    })), [count]);
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      <style>{`
        @keyframes snowfall { 0%{transform:translateY(-10px) rotate(0deg)} 100%{transform:translateY(calc(100vh + 10px)) rotate(360deg)} }
        @keyframes snowdrift { 0%,100%{margin-left:0} 50%{margin-left:15px} }
      `}</style>
      {flakes.map((f, i) => (
        <div key={i} style={{
          position: 'absolute', left: `${f.left}%`, top: -10,
          width: f.size, height: f.size, borderRadius: '50%',
          background: 'white', opacity: f.opacity,
          animation: `snowfall ${f.duration}s linear ${f.delay}s infinite, snowdrift ${f.duration * 0.7}s ease-in-out ${f.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

/* ───────────────────────────────────────────────
   WATER SPLASH BURST (triggered on advance)
   ─────────────────────────────────────────────── */
export function WaterSplash({ trigger, x, y }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!trigger || !ref.current) return;
    const el = ref.current;
    el.innerHTML = '';
    for (let i = 0; i < 8; i++) {
      const drop = document.createElement('div');
      const angle = (i / 8) * Math.PI * 2;
      drop.style.cssText = `position:absolute;width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,0.8);left:50%;top:50%;`;
      el.appendChild(drop);
      gsap.fromTo(drop, { x: 0, y: 0, scale: 1, opacity: 1 }, {
        x: Math.cos(angle) * (20 + Math.random() * 20), y: Math.sin(angle) * (15 + Math.random() * 15) - 10,
        scale: 0, opacity: 0, duration: 0.5 + Math.random() * 0.3, ease: 'power2.out',
      });
    }
  }, [trigger]);
  return <div ref={ref} style={{ position: 'absolute', left: x, top: y, pointerEvents: 'none', zIndex: 10 }} />;
}

/* ───────────────────────────────────────────────
   MAPLE LEAF SVG
   ─────────────────────────────────────────────── */
export function MapleLeaf({ size = 24, color = '#E03131', opacity = 1 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" opacity={opacity}>
      <path d="M16,2 L18,8 L24,6 L21,12 L28,14 L22,16 L26,22 L20,20 L18,28 L16,22 L14,28 L12,20 L6,22 L10,16 L4,14 L11,12 L8,6 L14,8 Z"
        fill={color} stroke={color} strokeWidth="0.5" strokeLinejoin="round" />
    </svg>
  );
}

/* ───────────────────────────────────────────────
   TEXTURED STONE BLOCK (for Tower)
   ─────────────────────────────────────────────── */
export function StoneBlock({ width, height = 32, index, isGold, children }) {
  const colors = [
    { face: '#8B7355', top: '#A08B70', side: '#6E5B45' },
    { face: '#9C8570', top: '#B09A85', side: '#7A6855' },
    { face: '#7A6650', top: '#8E7A65', side: '#5E4E3C' },
    { face: '#A08B70', top: '#B4A085', side: '#847060' },
  ];
  const c = isGold
    ? { face: '#F59F00', top: '#FFD43B', side: '#E67700' }
    : colors[index % colors.length];
  return (
    <div style={{
      width, height, position: 'relative', margin: '0 auto',
      borderRadius: 4, overflow: 'hidden',
    }}>
      {/* Main face */}
      <div style={{
        position: 'absolute', inset: 0, background: c.face,
        borderRadius: 4,
        boxShadow: isGold ? `0 0 12px rgba(245,159,0,0.5), inset 0 2px 0 ${c.top}, inset 0 -2px 0 ${c.side}`
          : `inset 0 2px 0 ${c.top}, inset 0 -2px 0 ${c.side}`,
      }}>
        {/* Texture lines */}
        <div style={{ position: 'absolute', top: '30%', left: '10%', right: '60%', height: 1, background: 'rgba(0,0,0,0.1)', borderRadius: 1 }} />
        <div style={{ position: 'absolute', top: '60%', left: '40%', right: '20%', height: 1, background: 'rgba(255,255,255,0.15)', borderRadius: 1 }} />
      </div>
      {children}
    </div>
  );
}

/* ───────────────────────────────────────────────
   HOCKEY PLAYER CHARACTER (for Le Duel)
   ─────────────────────────────────────────────── */
export function HockeyPlayer({ color = '#E03131', size = 120, facing = 'right' }) {
  const flip = facing === 'left' ? -1 : 1;
  return (
    <svg width={size} height={size * 1.2} viewBox="0 0 100 120" style={{ transform: `scaleX(${flip})` }}>
      {/* Skates */}
      <path d="M35,108 L30,108 Q25,108 25,104 L35,104 Z" fill="#333" />
      <path d="M60,108 L55,108 Q50,108 50,104 L60,104 Z" fill="#333" />
      <line x1="25" y1="108" x2="38" y2="108" stroke="#ccc" strokeWidth="2" />
      <line x1="50" y1="108" x2="63" y2="108" stroke="#ccc" strokeWidth="2" />
      {/* Legs */}
      <rect x="33" y="82" width="10" height="22" rx="4" fill={color} />
      <rect x="52" y="82" width="10" height="22" rx="4" fill={color} />
      {/* Body / Jersey */}
      <rect x="30" y="48" width="35" height="36" rx="8" fill={color} />
      <rect x="38" y="52" width="18" height="6" rx="2" fill="white" opacity="0.4" />
      {/* Jersey number */}
      <text x="48" y="76" textAnchor="middle" fontSize="14" fontWeight="bold" fill="white" fontFamily="Fredoka">#7</text>
      {/* Arms */}
      <rect x="18" y="52" width="14" height="8" rx="4" fill={color} />
      <rect x="63" y="52" width="14" height="8" rx="4" fill={color} />
      {/* Gloves */}
      <circle cx="18" cy="56" r="6" fill="#333" />
      <circle cx="77" cy="56" r="6" fill="#333" />
      {/* Helmet */}
      <path d="M34,42 Q36,22 48,18 Q60,22 62,42 Z" fill={color} />
      <path d="M36,38 Q48,28 60,38" fill={color} stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
      {/* Visor */}
      <path d="M38,38 Q48,34 58,38 L58,42 Q48,38 38,42 Z" fill="rgba(200,220,255,0.6)" />
      {/* Face */}
      <ellipse cx="48" cy="38" rx="10" ry="8" fill="#FFDAB9" />
      <circle cx="44" cy="37" r="1.5" fill="#333" />
      <circle cx="52" cy="37" r="1.5" fill="#333" />
      <path d="M44,42 Q48,45 52,42" stroke="#333" strokeWidth="1" fill="none" />
      {/* Hockey stick */}
      <line x1="16" y1="56" x2="8" y2="95" stroke="#8B5A2B" strokeWidth="3" strokeLinecap="round" />
      <path d="M5,92 L8,95 L15,95 L12,92 Z" fill="#333" />
    </svg>
  );
}

/* ───────────────────────────────────────────────
   DETAILED CANADA MAP SVG (province outlines)
   ─────────────────────────────────────────────── */
export function CanadaMapSVG({ litProvinces = [], width = '100%', height = '100%' }) {
  const provinces = [
    { id: 'bc', name: 'British Columbia', d: 'M60,180 L80,120 L95,100 L90,80 L75,85 L60,110 L45,120 L40,160 L50,180 Z', center: [65, 130] },
    { id: 'ab', name: 'Alberta', d: 'M95,100 L115,100 L115,180 L80,180 L80,120 Z', center: [98, 140] },
    { id: 'sk', name: 'Saskatchewan', d: 'M115,100 L145,100 L145,180 L115,180 Z', center: [130, 140] },
    { id: 'mb', name: 'Manitoba', d: 'M145,100 L175,95 L180,180 L145,180 Z', center: [160, 140] },
    { id: 'on', name: 'Ontario', d: 'M175,95 L210,85 L230,100 L240,150 L230,180 L200,190 L180,180 Z', center: [205, 140] },
    { id: 'qc', name: 'Quebec', d: 'M230,100 L260,70 L290,60 L310,80 L300,130 L270,160 L240,150 Z', center: [270, 110] },
    { id: 'nb', name: 'New Brunswick', d: 'M300,130 L315,125 L320,140 L310,150 L300,145 Z', center: [310, 138] },
    { id: 'ns', name: 'Nova Scotia', d: 'M320,140 L340,130 L345,140 L335,150 L320,148 Z', center: [333, 140] },
    { id: 'pe', name: 'PEI', d: 'M325,125 L340,122 L340,128 L325,130 Z', center: [333, 126] },
    { id: 'nl', name: 'Newfoundland', d: 'M320,80 L350,70 L360,90 L340,110 L320,100 Z', center: [340, 88] },
    { id: 'yt', name: 'Yukon', d: 'M45,40 L60,30 L75,40 L75,85 L60,110 L45,80 Z', center: [60, 60] },
    { id: 'nt', name: 'NWT', d: 'M75,40 L140,30 L160,50 L175,95 L145,100 L115,100 L95,100 L90,80 L75,85 Z', center: [120, 65] },
    { id: 'nu', name: 'Nunavut', d: 'M140,30 L200,15 L260,20 L290,60 L260,70 L230,100 L210,85 L175,95 L160,50 Z', center: [210, 50] },
  ];
  return (
    <svg width={width} height={height} viewBox="0 0 400 220" preserveAspectRatio="xMidYMid meet">
      <defs>
        <filter id="map-shadow"><feDropShadow dx="1" dy="1" stdDeviation="1" floodOpacity="0.15" /></filter>
      </defs>
      {provinces.map(p => {
        const isLit = litProvinces.includes(p.id);
        return (
          <g key={p.id}>
            <path d={p.d}
              fill={isLit ? '#2F9E44' : '#E8EDF4'}
              stroke="white" strokeWidth="1.5"
              filter="url(#map-shadow)"
              style={{ transition: 'fill 0.4s ease' }}
            />
            <text x={p.center[0]} y={p.center[1]} textAnchor="middle" fontSize="7" fill={isLit ? 'white' : '#868E96'} fontWeight="600" fontFamily="Nunito">{p.id.toUpperCase()}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ───────────────────────────────────────────────
   GAME HEADER BAR (consistent across games)
   ─────────────────────────────────────────────── */
export function GameHeader({ title, icon, color = 'var(--primary)', children }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.6rem 1.5rem',
      background: 'rgba(255,255,255,0.95)',
      borderBottom: `3px solid ${color}20`,
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      backdropFilter: 'blur(8px)',
      zIndex: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {icon}
        <h2 style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.4rem', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '0.02em' }}>
          {title}
        </h2>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
        {children}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────
   SCORE BADGE (animated score display)
   ─────────────────────────────────────────────── */
export function ScoreBadge({ label, value, color = 'var(--success)', scoreRef }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div ref={scoreRef} className="font-score" style={{ fontSize: '1.8rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

/* ───────────────────────────────────────────────
   CONTENT IMAGE (renders curriculum image if available)
   ─────────────────────────────────────────────── */
export function ContentImage({ src, alt, size = 200, style }) {
  if (!src) return null;
  const imgPath = process.env.REACT_APP_IMAGE_BASE_URL ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${src}` : `/assets/content/${src}`;
  return (
    <img src={imgPath} alt={alt || src.replace('.png', '').replace(/_/g, ' ')}
      style={{
        width: size, height: size, objectFit: 'contain',
        borderRadius: '1rem',
        background: 'rgba(255,255,255,0.5)',
        padding: 4,
        ...style,
      }}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
}

/* ───────────────────────────────────────────────
   QUESTION CARD (premium question display)
   ─────────────────────────────────────────────── */
/* ───────────────────────────────────────────────
   QUESTION CARD (premium question display)
   ─────────────────────────────────────────────── */
/* ───────────────────────────────────────────────
   ANSWER-LEAK SANITIZER
   Strips ONLY parentheticals that reveal the answer (e.g. "I have (j'ai)").
   Keeps disambiguation the student needs to answer — register cues like
   "(informal, to a friend)" / "(formal)", or the infinitive in "je (se lever)".
   Blind-stripping every "(...)" collapsed distinct prompts (e.g. "Please
   (formal)" and "Please (informal)") to an identical, unanswerable "Please".
   ─────────────────────────────────────────────── */
function _normForLeak(s) {
  return (s == null ? '' : String(s))
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

export function stripAnswerLeak(text, answer) {
  if (!text) return text;
  const normAns = _normForLeak(answer);
  // Without a usable answer to compare against, leave the prompt intact.
  if (normAns.length < 2) return text;
  return text
    .replace(/\s*\(([^)]*)\)/g, (match, inner) => {
      const normInner = _normForLeak(inner);
      if (!normInner) return match;
      // Strip only when the parenthetical IS / CONTAINS the answer (a real leak).
      if (normAns.includes(normInner) || normInner.includes(normAns)) return '';
      return match;
    })
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function QuestionCard({ promptEn, promptFr, promptImage, answer, accentColor = 'var(--primary)', children }) {
  const displayFr = promptFr ? stripAnswerLeak(promptFr, answer) : promptFr;
  const displayEn = promptEn ? stripAnswerLeak(promptEn, answer) : promptEn;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.95)', borderRadius: '1.25rem', padding: '1.25rem 1.5rem',
      border: `2px solid ${accentColor}15`,
      textAlign: 'center',
      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
      backdropFilter: 'blur(4px)',
    }}>
      {promptImage && <ContentImage src={promptImage} size={220} style={{ margin: '0 auto 0.75rem', display: 'block' }} />}
      {displayEn && (
        <div style={{
          fontSize: displayFr ? '1rem' : '1.5rem',
          color: displayFr ? 'var(--text-secondary)' : 'var(--text-primary)',
          fontWeight: displayFr ? 600 : 700,
          fontFamily: displayFr ? undefined : 'Fredoka, sans-serif',
          lineHeight: 1.3,
        }}>
          {displayEn}
        </div>
      )}
      {displayFr && (
        <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.8rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: displayEn ? '0.3rem' : 0, lineHeight: 1.2 }}>
          {displayFr}
        </div>
      )}
      {children}
    </div>
  );
}

/* ───────────────────────────────────────────────
   ANSWER OPTION BUTTON (premium style)
   ─────────────────────────────────────────────── */
export function OptionButton({ text, index, onSelect, disabled, feedback, accentColor, testId }) {
  const ref = useRef(null);
  const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
  return (
    <button ref={ref} data-testid={testId}
      className="option-btn"
      onClick={(e) => onSelect(index, e.currentTarget)}
      disabled={disabled}
      style={{ borderLeftColor: accentColor || 'var(--primary)', borderLeftWidth: 4, textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 36, borderRadius: '50%',
        background: `${accentColor || 'var(--primary)'}15`,
        color: accentColor || 'var(--primary)',
        fontSize: '1rem', fontWeight: 700, fontFamily: 'Fredoka',
        flexShrink: 0,
      }}>{letters[index] || index + 1}</span>
      <span style={{ flex: 1 }}>{text}</span>
    </button>
  );
}

/* ───────────────────────────────────────────────
   BEAVER CHARACTER (mascot)
   ─────────────────────────────────────────────── */
export function BeaverCharacter({ size = 60, happy = true }) {
  return (
    <svg width={size} height={size} viewBox="0 0 60 60">
      {/* Body */}
      <ellipse cx="30" cy="38" rx="18" ry="16" fill="#8B6914" />
      {/* Belly */}
      <ellipse cx="30" cy="42" rx="12" ry="10" fill="#D4A843" />
      {/* Head */}
      <circle cx="30" cy="20" r="14" fill="#A0781E" />
      {/* Ears */}
      <circle cx="18" cy="10" r="5" fill="#8B6914" />
      <circle cx="42" cy="10" r="5" fill="#8B6914" />
      <circle cx="18" cy="10" r="3" fill="#D4A843" />
      <circle cx="42" cy="10" r="3" fill="#D4A843" />
      {/* Eyes */}
      <circle cx="24" cy="18" r="3" fill="white" />
      <circle cx="36" cy="18" r="3" fill="white" />
      <circle cx="25" cy="18" r="1.5" fill="#333" />
      <circle cx="37" cy="18" r="1.5" fill="#333" />
      {/* Nose */}
      <ellipse cx="30" cy="24" rx="4" ry="3" fill="#5C3A0A" />
      {/* Teeth */}
      <rect x="27" y="27" width="3" height="4" rx="1" fill="white" />
      <rect x="31" y="27" width="3" height="4" rx="1" fill="white" />
      {/* Mouth */}
      {happy
        ? <path d="M24,28 Q30,33 36,28" stroke="#5C3A0A" strokeWidth="1.5" fill="none" />
        : <path d="M24,32 Q30,28 36,32" stroke="#5C3A0A" strokeWidth="1.5" fill="none" />
      }
      {/* Tail */}
      <ellipse cx="30" cy="54" rx="10" ry="4" fill="#5C3A0A" />
    </svg>
  );
}

/* ───────────────────────────────────────────────
   ANIMATED BACKGROUND PARTICLES (maple leaves, stars)
   ─────────────────────────────────────────────── */
export function FloatingParticles({ type = 'maple', count = 8 }) {
  const particles = useMemo(() =>
    Array.from({ length: count }).map(() => ({
      left: Math.random() * 100,
      delay: Math.random() * 10,
      duration: 8 + Math.random() * 12,
      size: 12 + Math.random() * 16,
      rotation: Math.random() * 360,
    })), [count]);
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 1 }}>
      <style>{`
        @keyframes floatDown { 0%{transform:translateY(-30px) rotate(0deg)} 100%{transform:translateY(calc(100% + 30px)) rotate(360deg)} }
        @keyframes drift { 0%,100%{margin-left:0} 50%{margin-left:20px} }
      `}</style>
      {particles.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', left: `${p.left}%`, top: -30,
          animation: `floatDown ${p.duration}s linear ${p.delay}s infinite, drift ${p.duration * 0.6}s ease-in-out ${p.delay}s infinite`,
        }}>
          {type === 'maple' && <MapleLeaf size={p.size} color="#E0313180" opacity={0.3} />}
          {type === 'star' && <div style={{ width: p.size * 0.5, height: p.size * 0.5, background: '#FFD43B', borderRadius: '50%', opacity: 0.4 }} />}
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────────────────────────────
   ZAMBONI / ROBOT CHARACTER (for Robot Balayeur)
   ─────────────────────────────────────────────── */
export function ZamboniCharacter({ size = 80, color = 'var(--primary)' }) {
  return (
    <svg width={size} height={size * 0.65} viewBox="0 0 120 78">
      {/* Machine body */}
      <rect x="20" y="20" width="80" height="35" rx="8" fill={color} />
      <rect x="25" y="25" width="30" height="15" rx="4" fill="rgba(200,220,255,0.5)" />
      {/* Windshield */}
      <path d="M25,25 L55,25 L55,40 L25,40 Z" fill="rgba(200,240,255,0.7)" stroke="white" strokeWidth="1" />
      {/* Driver */}
      <circle cx="40" cy="30" r="5" fill="#FFDAB9" />
      <circle cx="38" cy="29" r="1" fill="#333" />
      <circle cx="42" cy="29" r="1" fill="#333" />
      <path d="M36,27 Q40,24 44,27" fill={color} />
      {/* Exhaust/smokestack */}
      <rect x="85" y="12" width="8" height="12" rx="2" fill="#666" />
      <circle cx="89" cy="8" r="4" fill="#ddd" opacity="0.6">
        <animate attributeName="r" values="4;6;4" dur="1.5s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.6;0.2;0.6" dur="1.5s" repeatCount="indefinite" />
      </circle>
      {/* Wheels */}
      <circle cx="35" cy="60" r="10" fill="#333" stroke="#555" strokeWidth="2" />
      <circle cx="35" cy="60" r="4" fill="#888" />
      <circle cx="85" cy="60" r="10" fill="#333" stroke="#555" strokeWidth="2" />
      <circle cx="85" cy="60" r="4" fill="#888" />
      {/* Brush underneath */}
      <rect x="15" y="55" width="90" height="5" rx="2" fill="#8B5A2B" />
      {Array.from({ length: 15 }).map((_, i) => (
        <line key={i} x1={18 + i * 6} y1="60" x2={18 + i * 6} y2="68" stroke="#A0522D" strokeWidth="1.5" />
      ))}
      {/* Label */}
      <text x="75" y="42" textAnchor="middle" fontSize="8" fontWeight="bold" fill="white" fontFamily="Fredoka">#1</text>
    </svg>
  );
}

/* ───────────────────────────────────────────────
   DETECTIVE CHARACTER
   ─────────────────────────────────────────────── */
export function DetectiveCharacter({ size = 80 }) {
  return (
    <svg width={size} height={size * 1.1} viewBox="0 0 80 88">
      {/* Body / coat */}
      <rect x="22" y="42" width="36" height="36" rx="8" fill="#5C3D2E" />
      <rect x="28" y="44" width="24" height="8" rx="3" fill="#8B6914" />
      {/* Head */}
      <circle cx="40" cy="30" r="16" fill="#FFDAB9" />
      {/* Hat */}
      <ellipse cx="40" cy="18" rx="20" ry="5" fill="#5C3D2E" />
      <rect x="28" y="8" width="24" height="12" rx="4" fill="#5C3D2E" />
      <rect x="30" y="16" width="20" height="3" rx="1" fill="#8B6914" />
      {/* Eyes */}
      <circle cx="34" cy="28" r="2.5" fill="white" />
      <circle cx="46" cy="28" r="2.5" fill="white" />
      <circle cx="35" cy="28" r="1.5" fill="#333" />
      <circle cx="47" cy="28" r="1.5" fill="#333" />
      {/* Magnifying glass */}
      <circle cx="65" cy="45" r="10" fill="none" stroke="#8B6914" strokeWidth="3" />
      <circle cx="65" cy="45" r="7" fill="rgba(200,220,255,0.3)" />
      <line x1="58" y1="52" x2="50" y2="60" stroke="#8B6914" strokeWidth="3" strokeLinecap="round" />
      {/* Mustache */}
      <path d="M33,35 Q37,38 40,36 Q43,38 47,35" stroke="#5C3D2E" strokeWidth="2" fill="none" />
      {/* Smile */}
      <path d="M35,38 Q40,42 45,38" stroke="#333" strokeWidth="1" fill="none" />
    </svg>
  );
}

/* ───────────────────────────────────────────────
   ANIMATED CLOCK FACE (for Horloge Folle)
   ─────────────────────────────────────────────── */
export function AnimatedClock({ size = 180, hourAngle = 0, minuteAngle = 0, isWild = false }) {
  const clockRef = useRef(null);
  useEffect(() => {
    if (!isWild || !clockRef.current) return;
    const eyes = clockRef.current.querySelectorAll('.clock-eye');
    gsap.to(eyes, { scaleY: 1.3, duration: 0.3, yoyo: true, repeat: 3, ease: 'power2.inOut' });
    gsap.to(clockRef.current, { rotation: '+=360', duration: 0.5, ease: 'power2.inOut' });
  }, [isWild]);
  return (
    <svg ref={clockRef} width={size} height={size} viewBox="0 0 200 200">
      <defs>
        <radialGradient id="clock-face"><stop offset="0%" stopColor="#FFF9DB" /><stop offset="100%" stopColor="#FFE8A3" /></radialGradient>
        <filter id="clock-shadow"><feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.15" /></filter>
      </defs>
      {/* Outer ring */}
      <circle cx="100" cy="100" r="95" fill="var(--primary)" />
      {/* Face */}
      <circle cx="100" cy="100" r="88" fill="url(#clock-face)" filter="url(#clock-shadow)" />
      {/* Hour marks */}
      {[...Array(12)].map((_, i) => {
        const a = (i * 30 - 90) * Math.PI / 180;
        const r1 = 72, r2 = 82;
        return <line key={i} x1={100 + r1 * Math.cos(a)} y1={100 + r1 * Math.sin(a)} x2={100 + r2 * Math.cos(a)} y2={100 + r2 * Math.sin(a)} stroke="var(--text-primary)" strokeWidth={i % 3 === 0 ? 4 : 2} strokeLinecap="round" />;
      })}
      {/* Hour numbers */}
      {[...Array(12)].map((_, i) => {
        const num = i === 0 ? 12 : i;
        const a = (i * 30 - 90) * Math.PI / 180;
        return <text key={i} x={100 + 62 * Math.cos(a)} y={100 + 62 * Math.sin(a) + 5} textAnchor="middle" fontSize="14" fontWeight="700" fill="var(--text-primary)" fontFamily="Fredoka">{num}</text>;
      })}
      {/* Funny face */}
      <circle className="clock-eye" cx="80" cy="85" r="8" fill="white" stroke="#333" strokeWidth="1.5" />
      <circle cx="82" cy="85" r="4" fill="#333" />
      <circle className="clock-eye" cx="120" cy="85" r="8" fill="white" stroke="#333" strokeWidth="1.5" />
      <circle cx="122" cy="85" r="4" fill="#333" />
      <path d="M85,105 Q100,118 115,105" stroke="#E03131" strokeWidth="3" fill="none" strokeLinecap="round" />
      {/* Minute hand */}
      <line x1="100" y1="100" x2={100 + 55 * Math.cos((minuteAngle - 90) * Math.PI / 180)} y2={100 + 55 * Math.sin((minuteAngle - 90) * Math.PI / 180)} stroke="var(--text-primary)" strokeWidth="4" strokeLinecap="round" />
      {/* Hour hand */}
      <line x1="100" y1="100" x2={100 + 38 * Math.cos((hourAngle - 90) * Math.PI / 180)} y2={100 + 38 * Math.sin((hourAngle - 90) * Math.PI / 180)} stroke="var(--primary)" strokeWidth="5" strokeLinecap="round" />
      {/* Center dot */}
      <circle cx="100" cy="100" r="6" fill="var(--primary)" stroke="white" strokeWidth="2" />
    </svg>
  );
}

/* ───────────────────────────────────────────────
   CARD BACK DESIGN (for Memory game)
   ─────────────────────────────────────────────── */
export function PremiumCardBack({ variant = 0 }) {
  const designs = [
    { bg: 'linear-gradient(135deg, #E03131 0%, #C92A2A 100%)', pattern: 'maple' },
    { bg: 'linear-gradient(135deg, #1971C2 0%, #1864AB 100%)', pattern: 'hockey' },
    { bg: 'linear-gradient(135deg, #2F9E44 0%, #2B8A3E 100%)', pattern: 'nature' },
  ];
  const d = designs[variant % designs.length];
  return (
    <div style={{
      width: '100%', height: '100%', borderRadius: '0.6rem',
      background: d.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Pattern overlay */}
      <div style={{ position: 'absolute', inset: 0, opacity: 0.12 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={{ position: 'absolute', left: `${(i % 3) * 35 + 5}%`, top: `${Math.floor(i / 3) * 45 + 10}%` }}>
            <MapleLeaf size={20} color="white" />
          </div>
        ))}
      </div>
      {/* Center emblem */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        <MapleLeaf size={40} color="white" opacity={0.9} />
      </div>
      {/* Border decoration */}
      <div style={{
        position: 'absolute', inset: 6, borderRadius: '0.4rem',
        border: '2px solid rgba(255,255,255,0.25)',
      }} />
    </div>
  );
}

/* ───────────────────────────────────────────────
   TRAIN TRACK SCENE (for Tri Express / Batisseur)
   ─────────────────────────────────────────────── */
export function TrainTrackScene({ progress = 0, children }) {
  return (
    <div style={{ position: 'relative', width: '100%', height: 80 }}>
      <svg width="100%" height="80" viewBox="0 0 1000 80" preserveAspectRatio="none">
        {/* Tracks */}
        <line x1="0" y1="55" x2="1000" y2="55" stroke="#8B5A2B" strokeWidth="4" />
        <line x1="0" y1="65" x2="1000" y2="65" stroke="#8B5A2B" strokeWidth="4" />
        {/* Railroad ties */}
        {Array.from({ length: 40 }).map((_, i) => (
          <rect key={i} x={i * 25 + 5} y="50" width="10" height="20" fill="#5C3D2E" rx="1" />
        ))}
        {/* Progress marker - train */}
        <g transform={`translate(${progress * 9.5 + 20}, 25)`}>
          <rect x="0" y="0" width="40" height="25" rx="5" fill="var(--primary)" />
          <rect x="-5" y="2" width="10" height="15" rx="3" fill="var(--primary)" />
          <rect x="5" y="5" width="15" height="10" rx="2" fill="rgba(200,220,255,0.5)" />
          <circle cx="8" cy="28" r="5" fill="#333" />
          <circle cx="32" cy="28" r="5" fill="#333" />
          {/* Smoke */}
          <circle cx="-2" cy="-5" r="4" fill="#ddd" opacity="0.5">
            <animate attributeName="r" values="4;7;4" dur="1s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.5;0.1;0.5" dur="1s" repeatCount="indefinite" />
            <animate attributeName="cy" values="-5;-15;-5" dur="1s" repeatCount="indefinite" />
          </circle>
        </g>
      </svg>
      {children}
    </div>
  );
}
